import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Pencil, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FORM = {
  title: '',
  image_url: '',
  category: '',
  project_id: '',
};

export default function GalleryManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);

  // ── fetch ──
  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API_URL}/gallery`);
      setImages(res.data);
    } catch {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  // ── derived categories ──
  const categories = useMemo(() => {
    const cats = new Set();
    images.forEach((img) => {
      if (img.category) cats.add(img.category);
    });
    return ['all', ...Array.from(cats).sort()];
  }, [images]);

  // ── filtered images ──
  const filtered = useMemo(() => {
    if (activeCategory === 'all') return images;
    return images.filter((img) => img.category === activeCategory);
  }, [images, activeCategory]);

  // ── modal helpers ──
  const openAddModal = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_FORM });
    setIsModalOpen(true);
  };

  const openEditModal = (img) => {
    setEditingId(img.id);
    setFormData({
      title: img.title || '',
      image_url: img.image_url || '',
      category: img.category || '',
      project_id: img.project_id || '',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  // ── submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...formData };
      // send project_id only when provided
      if (!payload.project_id) delete payload.project_id;

      if (editingId) {
        await axios.put(`${API_URL}/gallery/${editingId}`, payload);
        toast.success('Image updated successfully!');
      } else {
        await axios.post(`${API_URL}/gallery`, payload);
        toast.success('Image added to gallery!');
      }
      closeModal();
      fetchGallery();
    } catch {
      toast.error(editingId ? 'Failed to update image' : 'Failed to add image');
    } finally {
      setSubmitting(false);
    }
  };

  // ── delete ──
  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this image?')) return;
    try {
      await axios.delete(`${API_URL}/gallery/${id}`);
      toast.success('Image deleted');
      fetchGallery();
    } catch {
      toast.error('Failed to delete image');
    }
  };

  // ── input class ──
  const inputClass =
    'w-full bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-2.5 text-white placeholder-neutral-600 focus:outline-none focus:border-amber-500 transition';

  // ── render ──
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Gallery Manager</h2>
          <p className="text-neutral-400 text-sm">Manage website images and media.</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-amber-500 hover:bg-amber-600 text-black font-medium px-4 py-2 rounded-xl flex items-center gap-2 transition"
        >
          <Plus size={20} /> Add Image
        </button>
      </div>

      {/* Category Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition ${
              activeCategory === cat
                ? 'bg-amber-500 text-black'
                : 'bg-neutral-800/60 text-neutral-400 hover:text-white hover:bg-neutral-800'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          <AnimatePresence>
            {filtered.map((img) => (
              <motion.div
                key={img.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="break-inside-avoid group relative bg-neutral-900 rounded-2xl overflow-hidden border border-neutral-800 cursor-pointer"
                onClick={() => openEditModal(img)}
              >
                <img
                  src={img.image_url}
                  alt={img.title || 'Gallery image'}
                  className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                  {img.title && <p className="text-white font-medium truncate mb-0.5">{img.title}</p>}
                  {img.category && <p className="text-amber-500 text-xs mb-2">{img.category}</p>}

                  <div className="flex items-center justify-between">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(img);
                      }}
                      className="bg-amber-500/20 text-amber-400 hover:bg-amber-500 hover:text-black p-2 rounded-lg transition"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={(e) => handleDelete(img.id, e)}
                      className="bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white p-2 rounded-lg transition"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {filtered.length === 0 && (
            <div className="col-span-full py-12 text-center text-neutral-500 border border-dashed border-neutral-800 rounded-2xl flex flex-col items-center w-full">
              <ImageIcon size={48} className="mb-4 opacity-50" />
              <p>No images found. Add some to build your gallery.</p>
            </div>
          )}
        </div>
      )}

      {/* ── Add / Edit Modal ─────────────────────────── */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={closeModal}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-neutral-900 border border-neutral-800 rounded-2xl p-6 w-full max-w-md shadow-2xl"
            >
              {/* Close button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-neutral-500 hover:text-white transition"
              >
                <X size={20} />
              </button>

              <h3 className="text-xl font-bold text-white mb-6">
                {editingId ? 'Edit Image' : 'Add New Image'}
              </h3>

              {/* Preview */}
              {formData.image_url && (
                <div className="mb-4 rounded-xl overflow-hidden border border-neutral-800 bg-neutral-950">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-full h-40 object-cover"
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Image URL */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Image URL *</label>
                  <input
                    required
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://..."
                    className={inputClass}
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className={inputClass}
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g. Interior, Exterior, Landscape"
                    className={inputClass}
                  />
                </div>

                {/* Project ID */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Project ID (optional)</label>
                  <input
                    type="text"
                    value={formData.project_id}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    placeholder="Link to a project"
                    className={inputClass}
                  />
                </div>

                {/* Actions */}
                <div className="pt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-neutral-700 text-neutral-300 hover:bg-neutral-800 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 text-black font-medium hover:bg-amber-600 transition disabled:opacity-50"
                  >
                    {submitting ? 'Saving…' : editingId ? 'Update Image' : 'Save Image'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
